<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { CheckCircleOutlined, ExclamationCircleOutlined, LoadingOutlined, MinusCircleOutlined } from '@ant-design/icons-vue'
import { get as obterManagedJob } from '~/api/generated/clients/managed-jobs/get'
import { cancel as cancelarManagedJob } from '~/api/generated/clients/managed-jobs/cancel'

interface SolicItem {
  agencia: string
  conta: string
  carteira: string
  contrato: string
}

interface PastaResult {
  pastaCodigo: string
  pastaNome?: string
  gcpj?: string
  resultado: string
  solics?: SolicItem[]
}

interface JobResponse {
  status: number
  progresso?: number
  resultadoJsonDocument?: PastaResult[]
}

interface Props {
  jobId: string
}

const props = defineProps<Props>()
const emit = defineEmits(['close'])

const status = ref<'loading' | 'done'>('loading')
const progress = ref(0)
const partialResults = ref<PastaResult[]>([])
const jobStatus = ref<number | null>(null)
const cancelling = ref(false)
let pollInterval: ReturnType<typeof setInterval> | null = null

const isSuccess = computed(() => jobStatus.value === 2)
const isCancelled = computed(() => jobStatus.value === 4)

const successResults = computed(() => partialResults.value.filter(r => r.resultado === 'OK'))
const totalOk = computed(() => successResults.value.length)
const totalErro = computed(() => partialResults.value.filter(r => r.resultado !== 'OK' && r.resultado !== 'Pendente').length)
const totalPendente = computed(() => partialResults.value.filter(r => r.resultado === 'Pendente').length)

const totalSolicsEnviados = computed(() =>
  successResults.value.reduce((sum, r) => sum + (r.solics?.length ?? 0), 0),
)

function displayName(item: PastaResult): string {
  return item.pastaNome || item.gcpj || item.pastaCodigo
}

const resultStatus = computed(() => {
  if (isSuccess.value && totalErro.value === 0) return 'success'
  if (isSuccess.value && totalErro.value > 0) return 'warning'
  if (isCancelled.value && totalOk.value > 0) return 'warning'
  if (isCancelled.value) return 'warning'
  return 'error'
})

const resultTitle = computed(() => {
  if (isSuccess.value && totalErro.value === 0) return 'Emails enviados com sucesso'
  if (isSuccess.value && totalErro.value > 0) return 'Processamento parcial'
  if (isCancelled.value) return 'Processamento cancelado'
  return 'Erro no processamento'
})

const resultSubTitle = computed(() => {
  if (partialResults.value.length === 0) return ''
  const parts: string[] = []
  if (isCancelled.value) {
    parts.push(`Cancelado pelo usuário`)
  }
  parts.push(`${totalOk.value} sucesso(s) de ${partialResults.value.length} pasta(s)`)
  if (totalSolicsEnviados.value > 0) {
    parts.push(`${totalSolicsEnviados.value} solic(s) enviado(s)`)
  }
  if (totalPendente.value > 0) {
    parts.push(`${totalPendente.value} não iniciada(s)`)
  }
  return parts.join(' | ')
})

function stopPolling() {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
}

async function fetchJobStatus() {
  try {
    const job = (await obterManagedJob(props.jobId)) as unknown as JobResponse

    if (job.progresso != null) {
      progress.value = Math.round(job.progresso * 100)
    }

    if (job.resultadoJsonDocument) {
      partialResults.value = job.resultadoJsonDocument
    }

    if (job.status === 5) {
      cancelling.value = true
    }

    if (![1, 5, 6].includes(job.status)) {
      stopPolling()
      jobStatus.value = job.status
      status.value = 'done'
    }
  } catch {
    stopPolling()
    jobStatus.value = 3
    status.value = 'done'
  }
}

function startPolling() {
  fetchJobStatus()
  pollInterval = setInterval(fetchJobStatus, 2000)
}

async function cancelar() {
  cancelling.value = true
  try {
    await cancelarManagedJob(props.jobId)
  } catch {
    // Se nao encontrou o job em execucao, o polling vai resolver o estado final
  }
}

onMounted(() => {
  startPolling()
})

onUnmounted(() => {
  stopPolling()
})
</script>

<template>
  <div style="padding-bottom: 24px">
    <DrawerTitle @close="() => emit('close')">
      <template #title>
        <span style="font-size: 16px; font-weight: 500; line-height: 35px">
          ANDAMENTO DO SOLIC
        </span>
      </template>
    </DrawerTitle>

    <div style="padding: 0 24px">
      <!-- Estado loading: progresso + resultados parciais -->
      <template v-if="status === 'loading'">
        <div style="margin-bottom: 24px">
          <a-progress
            :percent="progress"
            :status="cancelling ? 'normal' : 'active'"
          />
        </div>

        <div style="margin-bottom: 16px">
          <a-button
            danger
            :loading="cancelling"
            :disabled="cancelling"
            @click="cancelar"
          >
            Cancelar
          </a-button>
        </div>

        <a-alert
          v-if="cancelling"
          message="Cancelamento solicitado"
          description="Aguardando a finalização segura do job para que nenhum item fique inconsistente. Por favor, aguarde..."
          type="info"
          show-icon
          style="margin-bottom: 16px"
        />

        <a-list
          v-if="partialResults.length > 0"
          size="small"
          :data-source="partialResults"
          bordered
          style="max-height: 500px; overflow-y: auto"
        >
          <template #renderItem="{ item }">
            <a-list-item style="display: block">
              <div style="display: flex; justify-content: space-between; align-items: center">
                <div style="text-align: left; flex: 1">
                  <span style="font-weight: 500">{{ displayName(item) }}</span>
                  <span
                    v-if="item.resultado === 'Pendente'"
                    style="color: #999; margin-left: 8px; font-size: 12px"
                  >
                    - Aguardando
                  </span>
                  <span
                    v-else-if="item.resultado !== 'OK'"
                    style="color: #ff4d4f; margin-left: 8px; font-size: 12px"
                  >
                    - {{ item.resultado }}
                  </span>
                </div>
                <CheckCircleOutlined v-if="item.resultado === 'OK'" style="color: #52c41a; margin-left: 8px" />
                <MinusCircleOutlined v-else-if="item.resultado === 'Pendente'" style="color: #999; margin-left: 8px" />
                <ExclamationCircleOutlined v-else style="color: #ff4d4f; margin-left: 8px" />
              </div>
              <div v-if="item.solics && item.solics.length > 0" style="margin-top: 8px; padding-left: 12px; border-left: 2px solid #e8e8e8">
                <div
                  v-for="(solic, idx) in item.solics"
                  :key="idx"
                  style="font-size: 12px; color: #666; line-height: 20px"
                >
                  AG {{ solic.agencia }} | Conta {{ solic.conta }} | Carteira {{ solic.carteira }} | Contrato {{ solic.contrato }}
                </div>
              </div>
            </a-list-item>
          </template>
        </a-list>

        <div v-else style="text-align: center; padding: 24px; color: #999">
          <LoadingOutlined style="font-size: 24px; margin-bottom: 8px" />
          <div>Aguardando processamento...</div>
        </div>
      </template>

      <!-- Estado done: resultado final -->
      <template v-if="status === 'done'">
        <a-result
          :status="resultStatus"
          :title="resultTitle"
          :sub-title="resultSubTitle"
        >
          <template #extra>
            <a-list
              v-if="partialResults.length > 0"
              size="small"
              :data-source="partialResults"
              bordered
              style="margin-bottom: 16px; max-height: 400px; overflow-y: auto; text-align: left"
            >
              <template #renderItem="{ item }">
                <a-list-item style="display: block">
                  <div style="display: flex; justify-content: space-between; align-items: center">
                    <div style="text-align: left; flex: 1">
                      <span style="font-weight: 500">{{ displayName(item) }}</span>
                      <span
                        v-if="item.resultado === 'Pendente'"
                        style="color: #999; margin-left: 8px; font-size: 12px"
                      >
                        - Não iniciado
                      </span>
                      <span
                        v-else-if="item.resultado !== 'OK'"
                        style="color: #ff4d4f; margin-left: 8px; font-size: 12px"
                      >
                        - {{ item.resultado }}
                      </span>
                    </div>
                    <CheckCircleOutlined v-if="item.resultado === 'OK'" style="color: #52c41a; margin-left: 8px" />
                    <MinusCircleOutlined v-else-if="item.resultado === 'Pendente'" style="color: #999; margin-left: 8px" />
                    <ExclamationCircleOutlined v-else style="color: #ff4d4f; margin-left: 8px" />
                  </div>
                  <div v-if="item.solics && item.solics.length > 0" style="margin-top: 8px; padding-left: 12px; border-left: 2px solid #e8e8e8">
                    <div
                      v-for="(solic, idx) in item.solics"
                      :key="idx"
                      style="font-size: 12px; color: #666; line-height: 20px"
                    >
                      AG {{ solic.agencia }} | Conta {{ solic.conta }} | Carteira {{ solic.carteira }} | Contrato {{ solic.contrato }}
                    </div>
                  </div>
                </a-list-item>
              </template>
            </a-list>

            <a-button @click="() => emit('close')">Fechar</a-button>
          </template>
        </a-result>
      </template>
    </div>
  </div>
</template>
